// contractUSDTService.js

import { readContract, writeContract, waitForTransactionReceipt } from '@wagmi/core';
import { abi as usdtAbi } from './erc_abi';       // 假设此文件中导出了 USDT 合约的 ABI
import addresses from './addresses';            // 确保 addresses.usdtContractaddress 存在
import utils from '@/composables/utils';        // 包含 convertToWei 与 convertFromWei

const { convertToWei, convertFromWei } = utils;
const USDT_DECIMALS = 6;

import { config } from './walletConfig';

/**
 * 获取 USDT 余额
 * @param {string} account 钱包地址
 * @returns {Promise<string>} 格式化后的余额
 */
export async function getUSDTBalance(account) {
  try {
    const balance = await readContract(config, {
      abi: usdtAbi,
      address: addresses.usdtContractaddress,
      functionName: 'balanceOf',
      args: [account],
    });
    return convertFromWei(balance, USDT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in getUSDTBalance:", error);
    throw error;
  }
}

/**
 * 查询授权额度
 * @param {string} owner 钱包地址（授权方）
 * @param {string} spender 被授权地址
 * @returns {Promise<string>} 格式化后的授权额度
 */
export async function getUSDTAllowance(owner, spender) {
  try {
    const allowance = await readContract(config, {
      abi: usdtAbi,
      address: addresses.usdtContractaddress,
      functionName: 'allowance',
      args: [owner, spender],
    });
    return convertFromWei(allowance, USDT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in getUSDTAllowance:", error);
    throw error;
  }
}

/**
 * 授权 spender 花费一定数量的 USDT
 * @param {string} spender 被授权地址
 * @param {number|string} amount 授权数量（普通单位）
 * @returns {Promise<any>} 交易结果
 */
export async function approveUSDT(spender, amount) {
  try {
    const amountWei = convertToWei(amount, USDT_DECIMALS);
    const tx = await writeContract(config, {
      chainId: 137,
      abi: usdtAbi,
      address: addresses.usdtContractaddress,
      functionName: 'approve',
      args: [spender, amountWei],
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in approveUSDT:", error);
    throw error;
  }
}

/**
 * 向指定地址转账 USDT
 * @param {string} to 接收地址
 * @param {number|string} amount 转账数量（普通单位）
 * @returns {Promise<any>} 交易结果
 */
export async function transferUSDT(to, amount) {
  try {
    const amountWei = convertToWei(amount, USDT_DECIMALS);
    const tx = await writeContract(config, {
      chainId: 137,
      abi: usdtAbi,
      address: addresses.usdtContractaddress,
      functionName: 'transfer',
      args: [to, amountWei],
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in transferUSDT:", error);
    throw error;
  }
}

/**
 * 监控交易状态
 * @param {string} txHash 交易哈希
 * @returns {Promise<object>} 返回交易 receipt
 */
export async function monitorTransaction(txHash) {
  try {
    const receipt = await waitForTransactionReceipt(config, {hash: txHash });
    return receipt;
  } catch (error) {
    console.error("Error in monitorTransaction:", error);
    throw error;
  }
}